import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Search, Box, ChevronDown, Ban, Clock, X } from 'lucide-react';
import { getContainerColor } from '../utils/colors';

const Toolbar = ({
  filterText,
  invertFilter,
  onFilterChange,
  containers,
  selectedContainers,
  onToggleContainer,
  onToggleAllContainers,
  showContainers,
  onShowContainersChange,
  timestampMode,
  onTimestampModeChange,
  dropdownOpen,
  onDropdownToggle,
  statusText
}) => {
  const dropdownRef = useRef(null);
  const [timestampDropdownOpen, setTimestampDropdownOpen] = React.useState(false);
  const timestampDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onDropdownToggle(false);
      }
      if (timestampDropdownRef.current && !timestampDropdownRef.current.contains(e.target)) {
        setTimestampDropdownOpen(false);
      }
    };

    if (dropdownOpen || timestampDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen, timestampDropdownOpen, onDropdownToggle]);

  const containerArray = Array.from(containers).sort();
  const selectedCount = selectedContainers.length;

  const timestampModeLabels = {
    'full': 'Full',
    'time-only': 'Time Only',
    'hidden': 'Hidden'
  };

  return (
    <div className="bg-slate-800 p-2 border-b border-slate-700 flex gap-2 flex-wrap items-center text-sm">
      {/* Search */}
      <div className="relative group flex-1 max-w-md flex gap-1">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-500">
            <Search size={14} />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={(e) => onFilterChange('text', e.target.value)}
            placeholder="Filter by message content..."
            className="bg-slate-900 border border-slate-600 text-slate-200 text-sm rounded focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-8 p-1.5 placeholder-slate-500"
          />
          {filterText && (
            <button
              onClick={() => onFilterChange('text', '')}
              className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-500 hover:text-slate-300 transition-colors"
              title="Clear filter"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          onClick={() => onFilterChange('invertText', !invertFilter)}
          className={`px-2 py-1.5 rounded border transition ${
            invertFilter
              ? 'bg-red-600 border-red-500 text-white'
              : 'bg-slate-700 border-slate-600 text-slate-400 hover:bg-slate-600'
          }`}
          title={invertFilter ? 'Inverted: Hide matching lines' : 'Normal: Show matching lines'}
        >
          <Ban size={14} />
        </button>
      </div>

      <div className="h-6 w-px bg-slate-600 mx-1"></div>

      {/* Container Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDropdownToggle(!dropdownOpen);
          }}
          className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded flex items-center gap-2 border border-slate-600"
        >
          <Box size={14} />
          <span>Containers</span>
          {selectedCount < containers.size && (
            <span className="bg-blue-600 text-[10px] px-1.5 rounded-full">
              {selectedCount}
            </span>
          )}
          <ChevronDown size={14} />
        </button>

        {/* Dropdown Menu */}
        {dropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-slate-800 border border-slate-600 rounded shadow-xl z-50 max-h-96 overflow-y-auto p-2">
            <div className="flex justify-between text-xs text-slate-400 mb-2 px-1">
              <button
                onClick={() => onToggleAllContainers(true)}
                className="hover:text-white"
              >
                Select All
              </button>
              <button
                onClick={() => onToggleAllContainers(false)}
                className="hover:text-white"
              >
                Clear
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {containerArray.map(container => (
                <label
                  key={container}
                  className="flex items-center gap-2 text-sm text-slate-300 hover:bg-slate-700 p-1 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedContainers.includes(container)}
                    onChange={() => onToggleContainer(container)}
                    className="rounded bg-slate-900 border-slate-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={getContainerColor(container)}>{container}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toggle View */}
      <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 rounded border border-slate-600 bg-slate-700 hover:bg-slate-600 text-white">
        <input
          type="checkbox"
          checked={showContainers}
          onChange={(e) => onShowContainersChange(e.target.checked)}
          className="rounded bg-slate-700 border-slate-600 text-blue-600 focus:ring-blue-500"
        />
        <span>Show IDs</span>
      </label>

      {/* Timestamp Mode Dropdown */}
      <div className="relative" ref={timestampDropdownRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setTimestampDropdownOpen(!timestampDropdownOpen);
          }}
          className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-1.5 rounded flex items-center gap-2 border border-slate-600"
        >
          <Clock size={14} />
          <span>{timestampModeLabels[timestampMode]}</span>
          <ChevronDown size={14} />
        </button>

        {timestampDropdownOpen && (
          <div className="absolute top-full right-0 mt-1 w-40 bg-slate-800 border border-slate-600 rounded shadow-xl z-50">
            {Object.entries(timestampModeLabels).map(([mode, label]) => (
              <button
                key={mode}
                onClick={() => {
                  onTimestampModeChange(mode);
                  setTimestampDropdownOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 first:rounded-t last:rounded-b ${
                  timestampMode === mode ? 'bg-slate-700 text-white' : 'text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1"></div>

      <div className="text-xs text-slate-500">{statusText}</div>
    </div>
  );
};

Toolbar.propTypes = {
  filterText: PropTypes.string.isRequired,
  invertFilter: PropTypes.bool.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  containers: PropTypes.instanceOf(Set).isRequired,
  selectedContainers: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleContainer: PropTypes.func.isRequired,
  onToggleAllContainers: PropTypes.func.isRequired,
  showContainers: PropTypes.bool.isRequired,
  onShowContainersChange: PropTypes.func.isRequired,
  timestampMode: PropTypes.string.isRequired,
  onTimestampModeChange: PropTypes.func.isRequired,
  dropdownOpen: PropTypes.bool.isRequired,
  onDropdownToggle: PropTypes.func.isRequired,
  statusText: PropTypes.string.isRequired
};

export default Toolbar;

